// CreateCustomProcessDialog.js
// Extracted from AIWorkforcePage.js for better maintainability

import { useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../../components/ui/dialog';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Textarea } from '../../../components/ui/textarea';
import { Label } from '../../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { Badge } from '../../../components/ui/badge';
import { Workflow, Plus, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import { 
  SCHEDULE_TYPES, 
  PROCESS_TASK_TYPES, 
  PROCESS_PRIORITY_OPTIONS,
  DEFAULT_PROCESS_TASK_FORM 
} from '../constants';

/**
 * Dialog for creating a custom reusable process with multiple tasks
 * 
 * @param {Object} props
 * @param {boolean} props.open - Controls dialog visibility
 * @param {function} props.onOpenChange - Callback when dialog open state changes
 * @param {Object} props.formData - Current process form data { name, description, tasks, schedule_type }
 * @param {function} props.onFormChange - Callback when form data changes
 * @param {function} props.onSubmit - Callback when form is submitted
 * @param {function} props.onCancel - Callback when cancel is clicked
 */
export const CreateCustomProcessDialog = ({
  open,
  onOpenChange,
  formData,
  onFormChange,
  onSubmit,
  onCancel
}) => {
  // Local state for the task being added to the process
  const [taskForm, setTaskForm] = useState(DEFAULT_PROCESS_TASK_FORM);

  const handleFieldChange = (field, value) => {
    onFormChange({ ...formData, [field]: value });
  };

  const handleAddTask = () => {
    if (!taskForm.title) {
      toast.error('Task title is required');
      return;
    }
    
    onFormChange({
      ...formData,
      tasks: [...formData.tasks, { ...taskForm, id: Date.now() }]
    });
    setTaskForm(DEFAULT_PROCESS_TASK_FORM);
  };

  const handleRemoveTask = (taskId) => {
    onFormChange({
      ...formData,
      tasks: formData.tasks.filter(t => t.id !== taskId)
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Workflow className="h-5 w-5" />
            Create Custom Process
          </DialogTitle>
          <DialogDescription>
            Define a reusable workflow template with multiple tasks for overnight execution
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Process Name *</Label>
              <Input
                value={formData.name || ''}
                onChange={(e) => handleFieldChange('name', e.target.value)}
                placeholder="e.g., Weekly Lead Research"
                data-testid="process-name-input"
              />
            </div>
            <div>
              <Label>Schedule Type</Label>
              <Select 
                value={formData.schedule_type || 'manual'} 
                onValueChange={(val) => handleFieldChange('schedule_type', val)}
              >
                <SelectTrigger data-testid="process-schedule-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {SCHEDULE_TYPES.map(type => (
                    <SelectItem key={type.value} value={type.value}>
                      {type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <Label>Description</Label>
            <Textarea
              value={formData.description || ''}
              onChange={(e) => handleFieldChange('description', e.target.value)}
              placeholder="Describe what this process does..."
              rows={2}
              data-testid="process-description"
            />
          </div>

          {/* Task List */}
          <div className="border rounded-lg p-4">
            <div className="flex items-center justify-between mb-3">
              <Label className="text-base">Tasks in Process ({formData.tasks?.length || 0})</Label>
            </div>
            
            {formData.tasks?.length > 0 && (
              <div className="space-y-2 mb-4 max-h-[200px] overflow-y-auto">
                {formData.tasks.map((task, idx) => (
                  <div key={task.id} className="flex items-center gap-2 p-2 bg-slate-50 rounded" data-testid={`process-task-${idx}`}>
                    <span className="text-xs text-muted-foreground w-6">{idx + 1}.</span>
                    <div className="flex-1">
                      <span className="font-medium text-sm">{task.title}</span>
                      <span className="text-xs text-muted-foreground ml-2">({task.task_type})</span>
                    </div>
                    <Badge variant="outline" className="text-xs">P{task.priority}</Badge>
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => handleRemoveTask(task.id)}
                      data-testid={`remove-task-${idx}`}
                    >
                      <XCircle className="h-4 w-4 text-red-500" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
            
            {/* Add Task Form */}
            <div className="border-t pt-3 mt-3">
              <p className="text-sm font-medium mb-2">Add New Task</p>
              <div className="grid grid-cols-2 gap-2 mb-2">
                <div>
                  <Label className="text-xs">Task Type</Label>
                  <Select 
                    value={taskForm.task_type} 
                    onValueChange={(val) => setTaskForm({...taskForm, task_type: val})}
                  >
                    <SelectTrigger className="h-8 text-sm" data-testid="new-task-type">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {PROCESS_TASK_TYPES.map(type => (
                        <SelectItem key={type.value} value={type.value}>
                          {type.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Priority</Label>
                  <Select 
                    value={String(taskForm.priority)} 
                    onValueChange={(val) => setTaskForm({...taskForm, priority: parseInt(val)})}
                  >
                    <SelectTrigger className="h-8 text-sm" data-testid="new-task-priority">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {PROCESS_PRIORITY_OPTIONS.map(opt => (
                        <SelectItem key={opt.value} value={String(opt.value)}>
                          {opt.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-2">
                <Input
                  value={taskForm.title}
                  onChange={(e) => setTaskForm({...taskForm, title: e.target.value})}
                  placeholder="Task title"
                  className="h-8 text-sm"
                  data-testid="new-task-title"
                />
                <Textarea
                  value={taskForm.description}
                  onChange={(e) => setTaskForm({...taskForm, description: e.target.value})}
                  placeholder="Task description..."
                  rows={2}
                  className="text-sm"
                  data-testid="new-task-description"
                />
                <Button 
                  size="sm" 
                  variant="outline" 
                  onClick={handleAddTask}
                  disabled={!taskForm.title}
                  className="w-full"
                  data-testid="add-task-to-process-btn"
                >
                  <Plus className="h-3 w-3 mr-1" />
                  Add Task to Process
                </Button>
              </div>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button 
            variant="outline" 
            onClick={onCancel}
            data-testid="process-cancel-btn"
          >
            Cancel
          </Button>
          <Button 
            onClick={onSubmit}
            disabled={!formData.name || !formData.tasks?.length}
            data-testid="submit-process-btn"
          >
            <Workflow className="h-4 w-4 mr-2" />
            Create Process
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CreateCustomProcessDialog;
